import React from "react";
import { LOGO_URL_SYMBOL } from "@/lib/api";
import { Building2, Instagram, Mail, MapPin } from "lucide-react";
import { openCookieSettings } from "@/components/CookieConsent";
import CityLinks from "@/components/CityLinks";
import { COMPANY_NAME, COMPANY_PEC, COMPANY_VAT, SUPPORT_EMAIL } from "@/lib/company";

// TikTok SVG icon (inline since lucide doesn't include it)
const TikTokIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.77a8.16 8.16 0 0 0 4.93 1.66V7a4.85 4.85 0 0 1-2-.31Z"/>
    </svg>
);

export default function Footer() {
    return (
        <footer data-testid="footer" className="footer-plateau bg-ink-950 mt-32 rounded-t-[2.5rem] sm:rounded-t-[3rem] no-neu">
            <div className="w-full px-6 sm:px-12 lg:px-20 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <div data-testid="footer-logo-badge" className="logo-badge-3d" style={{ width: 56, height: 56 }}>
                                <img src={LOGO_URL_SYMBOL} alt="VendiDirect" className="logo-badge-3d-img" draggable="false" />
                            </div>
                            <div>
                                <div className="font-serif text-2xl">vendi<span className="text-gold">direct</span>.it</div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-400 mt-1">la tua casa. direttamente.</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 mt-7">
                            <a
                                data-testid="social-instagram"
                                href="https://www.instagram.com/vendidirectofficial?igsi=MTA5azM5OTE3NGsxYg%3D%3D&utm_source=qr"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Instagram vendidirectofficial"
                                className="group relative w-11 h-11 flex items-center justify-center border border-white/15 hover:border-gold transition-all duration-300 rounded-2xl"
                            >
                                <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500/0 via-fuchsia-500/0 to-orange-400/0 group-hover:from-pink-500/40 group-hover:via-fuchsia-500/30 group-hover:to-orange-400/40 transition-all duration-500" />
                                <span className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 blur-md bg-gradient-to-br from-pink-500 via-fuchsia-500 to-orange-400 transition-opacity duration-500 pointer-events-none" />
                                <Instagram size={18} className="relative text-ink-100 group-hover:text-white transition-colors" />
                            </a>
                            <a
                                data-testid="social-tiktok"
                                href="https://www.tiktok.com/@vendidirectofficial"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="TikTok vendidirectofficial"
                                className="group relative w-11 h-11 flex items-center justify-center border border-white/15 hover:border-gold transition-all duration-300 rounded-2xl"
                            >
                                <span className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 blur-md bg-gradient-to-r from-cyan-400 via-pink-500 to-cyan-400 transition-opacity duration-500 pointer-events-none" />
                                <span className="relative text-ink-100 group-hover:text-white transition-colors">
                                    <TikTokIcon />
                                </span>
                            </a>
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <div className="label-eyebrow mb-5">Naviga</div>
                        <ul className="space-y-3 text-sm text-ink-100">
                            <li><a href="/annunci" className="hover:text-gold transition-colors">Annunci</a></li>
                            <li><a href="/prezzi" className="hover:text-gold transition-colors">Abbonamenti</a></li>
                            <li><a href="/pubblica" className="hover:text-gold transition-colors">Pubblica</a></li>
                            <li><a data-testid="footer-link-tutorial" href="/tutorial" className="hover:text-gold transition-colors">Tutorial</a></li>
                        </ul>
                        <div className="label-eyebrow mt-8 mb-4">Case per città</div>
                        <CityLinks variant="footer" />
                        <div className="label-eyebrow mt-8 mb-4">Legale</div>
                        <ul className="space-y-3 text-sm text-ink-100">
                            <li><a data-testid="footer-link-privacy" href="/privacy" className="hover:text-gold transition-colors">Privacy GDPR</a></li>
                            <li><a data-testid="footer-link-cookie" href="/cookie" className="hover:text-gold transition-colors">Cookie Policy</a></li>
                            <li><a data-testid="footer-link-terms" href="/termini" className="hover:text-gold transition-colors">Termini di servizio</a></li>
                            <li>
                                <button
                                    data-testid="footer-cookie-settings"
                                    onClick={openCookieSettings}
                                    className="hover:text-gold transition-colors text-left"
                                >
                                    Impostazioni cookie
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div className="md:col-span-4">
                        <div className="label-eyebrow mb-5">Contatti</div>
                        <a data-testid="footer-support-email" href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-ink-100 hover:text-gold transition-colors inline-flex items-center gap-2">
                            <Mail size={13} /> {SUPPORT_EMAIL}
                        </a>
                        <address className="footer-company-panel not-italic mt-6" data-testid="footer-company-details">
                            <div className="footer-company-row">
                                <Building2 size={14} />
                                <span><b>Ragione Sociale</b>{COMPANY_NAME}</span>
                            </div>
                            <div className="footer-company-row">
                                <MapPin size={14} />
                                <span><b>Sede Legale</b>Segrate (MI)</span>
                            </div>
                            <div className="footer-company-row">
                                <span className="footer-company-mark">IT</span>
                                <span><b>P.IVA / C.F.</b>{COMPANY_VAT}</span>
                            </div>
                            <div className="footer-company-row">
                                <Mail size={14} />
                                <span><b>PEC</b><a data-testid="footer-company-pec" href={`mailto:${COMPANY_PEC}`} className="hover:text-gold transition-colors break-all">{COMPANY_PEC}</a></span>
                            </div>
                        </address>
                    </div>
                </div>
                <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between gap-4 text-xs text-ink-400">
                    <span>© {new Date().getFullYear()} {COMPANY_NAME} — Tutti i diritti riservati</span>
                    <span>Made with passione in Italia</span>
                </div>
            </div>
        </footer>
    );
}
